import { Injectable, inject } from '@angular/core';
import { CapicheLoggerService } from 'capiche-logger';
import { UsageCounterRepository } from '../data-access-layer/usage-counter-repository.service';
import { InMemoryUsageCounterStorage, UsageCounterStorage } from './usage-counter-storage';
import {
  CAPICHE_USAGE_COUNTER_CONFIG,
  CAPICHE_USAGE_COUNTER_STORAGE,
  DEFAULT_USAGE_COUNTER_FLUSH_INTERVAL_MS,
  DEFAULT_USAGE_COUNTER_FLUSH_THRESHOLD,
} from './usage-counter.config';

/**
 * Client-side batched usage counter (CSW-1643).
 *
 * getSuggestions can fire thousands of times a session (debounced per
 * keystroke) — sending one API/Firestore write per call would hammer a single
 * daily-counter document. Instead this service accumulates a count in memory
 * and flushes it periodically and on blur/hidden, so a heavy session becomes
 * a handful of writes instead of thousands.
 *
 * Batching cadence is configurable via CAPICHE_USAGE_COUNTER_CONFIG; storage
 * is swappable via CAPICHE_USAGE_COUNTER_STORAGE (in-memory by default — see
 * usage-counter-storage.ts for how to add localStorage-backed durability
 * later without changing this service).
 *
 * Only `getSuggestions` is batched. Replacement counts are incremented
 * inline server-side (see WordReplacementsService) since accepting a
 * suggestion is a deliberate, low-frequency action that doesn't need this.
 */
@Injectable({
  providedIn: 'root',
})
export class UsageCounterService {
  private logger = new CapicheLoggerService('usage-counter.service:', false);

  private readonly repository = inject(UsageCounterRepository);
  private readonly config = inject(CAPICHE_USAGE_COUNTER_CONFIG, { optional: true });
  private readonly storage: UsageCounterStorage =
    inject(CAPICHE_USAGE_COUNTER_STORAGE, { optional: true }) ?? new InMemoryUsageCounterStorage();

  private readonly flushIntervalMs = this.config?.flushIntervalMs ?? DEFAULT_USAGE_COUNTER_FLUSH_INTERVAL_MS;
  private readonly flushThreshold = this.config?.flushThreshold ?? DEFAULT_USAGE_COUNTER_FLUSH_THRESHOLD;

  /** Guards against overlapping flushes (e.g. interval firing mid-flush). */
  private flushInFlight: Promise<void> | null = null;

  constructor() {
    if (typeof window === 'undefined') {
      // Not a browser context (e.g. unit tests) — no timers/listeners to wire up.
      return;
    }

    setInterval(() => void this.flush(), this.flushIntervalMs);

    // 'blur' catches switching away within the browser; 'visibilitychange' -> hidden
    // also catches tab close / navigation, and is the modern, more reliable signal
    // for "the user is leaving" (unlike 'unload', which is unreliable and
    // increasingly restricted by browsers).
    window.addEventListener('blur', () => void this.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void this.flush();
      }
    });
  }

  /** Records `amount` getSuggestions calls (default 1) against the local batch. */
  recordGetSuggestions(amount = 1): void {
    if (amount <= 0) return;
    const next = this.storage.load() + amount;
    this.storage.save(next);

    if (next >= this.flushThreshold) {
      void this.flush();
    }
  }

  /**
   * Sends the currently-batched count to the server and clears it locally.
   * On failure, the count is added back so it's retried on the next flush
   * rather than silently lost.
   */
  async flush(): Promise<void> {
    if (this.flushInFlight) {
      return this.flushInFlight;
    }

    const count = this.storage.load();
    if (count <= 0) {
      return;
    }

    this.storage.clear();

    this.flushInFlight = (async () => {
      try {
        const response = await this.repository.recordGetSuggestions(count);
        if (!response || response.error) {
          this.logger.warn('flush: request failed, requeueing count:', count, response?.error);
          this.storage.save(this.storage.load() + count);
        }
      } catch (e) {
        this.logger.error('flush: error, requeueing count:', count, e);
        this.storage.save(this.storage.load() + count);
      } finally {
        this.flushInFlight = null;
      }
    })();

    return this.flushInFlight;
  }
}
